/**
 * Writing Training「常用表达」脚手架（D 类）。
 *
 * 权威键：Platform Scope `languageId`（`french` / `english` / …）——跟语言走，不跟 Exam/Task。
 * 分类按**语篇衔接关系**（引入/承接/因果/转折/并列…），不是 Exam Task。
 * 非 Platform Scope；非 Content Inventory；非 Admin CRUD。
 */

import { ENGLISH_WRITING_EXPRESSIONS } from './writingExpressions/english';
import { FRENCH_WRITING_EXPRESSIONS } from './writingExpressions/french';
import { KOREAN_WRITING_EXPRESSIONS } from './writingExpressions/korean';
import { SPANISH_WRITING_EXPRESSIONS } from './writingExpressions/spanish';

/**
 * Discourse-relation categories for writing scaffolds.
 * `result` merged into `cause_result`（「结果关系」≈「因果关系-结果」）.
 */
export const WRITING_EXPRESSION_CATEGORY_IDS = [
  'introduce',
  'continue',
  'finally',
  'cause_reason',
  'cause_result',
  'contrast_turn',
  'contrast_concede',
  'parallel_add',
  'parallel_choice',
  'progressive',
  'compare',
  'juxtapose',
  'exemplify',
  'exception',
  'emphasize',
  'condition',
  'summarize',
  'purpose',
  'restate',
  'temporal',
  'basis',
] as const;

export type WritingExpressionCategoryId = (typeof WRITING_EXPRESSION_CATEGORY_IDS)[number];

/** UI / docs labels (zh) — not inserted into the essay. */
export const WRITING_EXPRESSION_CATEGORY_LABELS: Readonly<
  Record<WritingExpressionCategoryId, string>
> = {
  introduce: '引入',
  continue: '承接',
  finally: '最后',
  cause_reason: '因果关系·原因',
  cause_result: '因果关系·结果',
  contrast_turn: '转折关系·转折',
  contrast_concede: '转折关系·让步',
  parallel_add: '并列关系·添加',
  parallel_choice: '并列关系·选择',
  progressive: '递进关系',
  compare: '比较关系',
  juxtapose: '对照关系',
  exemplify: '举例关系',
  exception: '例外关系',
  emphasize: '强调关系',
  condition: '条件关系',
  summarize: '归纳总结',
  purpose: '目的关系',
  restate: '重申关系',
  temporal: '时间关系',
  basis: '依据关系',
};

export type WritingExpressionItem = {
  /** Stable id within the language pack (e.g. `fr-introduce-1`). */
  id: string;
  category: WritingExpressionCategoryId;
  /** Target-language text inserted into the draft. */
  text: string;
  /** Optional Chinese gloss for future UI; never inserted. */
  gloss?: string;
};

const BY_LANGUAGE: Readonly<Record<string, readonly WritingExpressionItem[]>> = {
  french: FRENCH_WRITING_EXPRESSIONS,
  english: ENGLISH_WRITING_EXPRESSIONS,
  spanish: SPANISH_WRITING_EXPRESSIONS,
  korean: KOREAN_WRITING_EXPRESSIONS,
};

function normalizeLanguageId(languageId: string | null | undefined): string {
  return String(languageId ?? '')
    .trim()
    .toLowerCase();
}

/** True when a curated pack exists for this Platform Scope languageId. */
export function hasWritingExpressionPack(languageId: string | null | undefined): boolean {
  const key = normalizeLanguageId(languageId);
  if (!key) return false;
  const pack = BY_LANGUAGE[key];
  return Array.isArray(pack) && pack.length > 0;
}

/**
 * Full expression list for a language.
 * Unknown / empty language → `[]`. Does not take examId or taskNumber.
 */
export function getWritingExpressionsForLanguage(
  languageId: string | null | undefined
): WritingExpressionItem[] {
  const key = normalizeLanguageId(languageId);
  if (!key) return [];
  const pack = BY_LANGUAGE[key];
  return pack ? [...pack] : [];
}

/** Language ids that currently ship a non-empty pack. */
export function getWritingExpressionLanguageIds(): string[] {
  return Object.keys(BY_LANGUAGE).filter((id) => hasWritingExpressionPack(id));
}

export function isWritingExpressionCategoryId(value: string): value is WritingExpressionCategoryId {
  return (WRITING_EXPRESSION_CATEGORY_IDS as readonly string[]).includes(value);
}
